/**
 * Mobile-safe return context for the Didit identity-verification round-trip
 * (PR-DIDIT-MOBILE-RETURN-1).
 *
 * Why this exists
 * ---------------
 * The `/registrazione-intermediario/verifica-identita` page restores its state
 * from React Router state + sessionStorage. When the user is redirected OUT to
 * `verify.didit.me` and back, mobile browsers frequently drop the in-memory
 * router state AND the sessionStorage of the original browsing context (new
 * context / cleared on cross-app navigation). The page then can't find the
 * uploadToken and shows "Sessione scaduta", even though the BE token is still
 * valid.
 *
 * This module persists a SHORT-LIVED recovery context in `localStorage` (which
 * survives the external round-trip on mobile) so the page can restore the token
 * on return — WITHOUT ever putting the token in the URL / query string.
 *
 * Privacy / security
 * ------------------
 *   - Stores ONLY: the uploadToken (a random secret bound to one staging
 *     request, already held in sessionStorage/router state — same-origin trust),
 *     the coarse non-PII classification (legalNature / orgType) needed to resume
 *     the documents step, the request publicId, a flow marker and a TTL. NO name
 *     / email / fiscal code / address / Didit raw payload.
 *   - Short TTL (45 min): an abandoned context self-expires; an expired/invalid
 *     read clears the key. The context is also cleared when the user proceeds to
 *     the documents step or when the token is rejected.
 */
import {
  isValidDocumentsPageState,
  type DocumentsPageState,
} from './documents-state-recovery';

export const IDENTITY_VERIFICATION_RETURN_CONTEXT_KEY =
  'abp:onboarding:identity-verification:return-context';

const FLOW_MARKER = 'identityVerification' as const;

/** Max lifetime of the recovery context — short, per spec (≤ 60 min). */
export const RETURN_CONTEXT_TTL_MS = 45 * 60 * 1000;

export interface IdentityVerificationReturnContextInput {
  uploadToken: string;
  legalNature: DocumentsPageState['legalNature'];
  orgType: DocumentsPageState['orgType'];
  /** Onboarding request publicId (= submissionId), when already known. */
  requestPublicId?: string;
}

interface StoredReturnContext {
  flow: typeof FLOW_MARKER;
  uploadToken: string;
  legalNature: DocumentsPageState['legalNature'];
  orgType: DocumentsPageState['orgType'];
  requestPublicId?: string;
  createdAt: number;
  expiresAt: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    // Storage disabled (Safari private mode / strict policies) → no-op channel.
    return null;
  }
}

export interface ReturnContextOpts {
  /** Injected storage for tests; defaults to `window.localStorage`. */
  storage?: StorageLike | null;
  /** Injected clock (ms since epoch) for deterministic tests. */
  nowMs?: number;
}

/** Persist (or refresh) the recovery context. Best-effort; never throws. */
export function saveIdentityVerificationReturnContext(
  input: IdentityVerificationReturnContextInput,
  opts: ReturnContextOpts = {},
): void {
  const storage = opts.storage ?? defaultStorage();
  if (!storage) return;
  const now = opts.nowMs ?? Date.now();
  const payload: StoredReturnContext = {
    flow: FLOW_MARKER,
    uploadToken: input.uploadToken,
    legalNature: input.legalNature,
    orgType: input.orgType,
    ...(input.requestPublicId ? { requestPublicId: input.requestPublicId } : {}),
    createdAt: now,
    expiresAt: now + RETURN_CONTEXT_TTL_MS,
  };
  try {
    storage.setItem(
      IDENTITY_VERIFICATION_RETURN_CONTEXT_KEY,
      JSON.stringify(payload),
    );
  } catch {
    /* best-effort */
  }
}

/**
 * Load + validate the recovery context. Returns a `DocumentsPageState`-compatible
 * object on success, or null when absent / malformed / wrong-flow / expired.
 * An expired or invalid entry is removed as a side effect (no stale token).
 */
export function loadIdentityVerificationReturnContext(
  opts: ReturnContextOpts = {},
): DocumentsPageState | null {
  const storage = opts.storage ?? defaultStorage();
  if (!storage) return null;

  let raw: string | null;
  try {
    raw = storage.getItem(IDENTITY_VERIFICATION_RETURN_CONTEXT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearIdentityVerificationReturnContext({ storage });
    return null;
  }

  if (!isStoredReturnContext(parsed)) {
    clearIdentityVerificationReturnContext({ storage });
    return null;
  }

  const now = opts.nowMs ?? Date.now();
  if (now >= parsed.expiresAt) {
    clearIdentityVerificationReturnContext({ storage });
    return null;
  }

  const state: DocumentsPageState = {
    uploadToken: parsed.uploadToken,
    legalNature: parsed.legalNature,
    orgType: parsed.orgType,
    ...(parsed.requestPublicId ? { submissionId: parsed.requestPublicId } : {}),
  };
  // Reuse the canonical validator so a corrupted enum can't slip through.
  return isValidDocumentsPageState(state) ? state : null;
}

/** Remove the recovery context (proceed to documents / invalid token / expiry). */
export function clearIdentityVerificationReturnContext(
  opts: Pick<ReturnContextOpts, 'storage'> = {},
): void {
  const storage = opts.storage ?? defaultStorage();
  if (!storage) return;
  try {
    storage.removeItem(IDENTITY_VERIFICATION_RETURN_CONTEXT_KEY);
  } catch {
    /* best-effort */
  }
}

function isStoredReturnContext(value: unknown): value is StoredReturnContext {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v.flow === FLOW_MARKER &&
    typeof v.uploadToken === 'string' &&
    v.uploadToken.length > 0 &&
    (v.legalNature === 'PERSON' || v.legalNature === 'COMPANY') &&
    (v.orgType === 'BROKER' ||
      v.orgType === 'AGENZIA' ||
      v.orgType === 'INTERMEDIARIO') &&
    (v.requestPublicId === undefined || typeof v.requestPublicId === 'string') &&
    typeof v.createdAt === 'number' &&
    typeof v.expiresAt === 'number'
  );
}
